"use client";

import { useCallback, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Puzzle,
  Heart,
  Sparkles,
  RotateCcw,
  Delete,
  RefreshCw,
} from "lucide-react";
import type { Gift } from "@/types";
import { cn } from "@/lib/utils";
import {
  getExperienceData,
  normalizeWordForGame,
  type WordGameExperienceData,
} from "@/data/experienceDataSchema";

export type WordGameVariant = "default" | "spotify";

interface WordGamePalette {
  accent: string;
  accentRgb: string;
}

interface WordGameSectionProps {
  gift: Partial<Gift>;
  variant: WordGameVariant;
  palette?: WordGamePalette;
  className?: string;
}

// ─── Helpers locais ─────────────────────────────────────────────────────────

const FALLBACK_WORDS = [
  "AMOR",
  "VIDA",
  "LUZ",
  "LAR",
  "SONHO",
  "CARINHO",
  "SAUDADE",
  "JUNTOS",
  "PAIXAO",
  "ALMA",
  "AFETO",
  "BEIJO",
];

const HINTS = [
  "O que aquece o peito quando vocês estão juntos.",
  "Uma palavra curta que cabe em um suspiro.",
  "Algo que se sente sem precisar dizer.",
  "Aquilo que faz qualquer dia ficar inteiro.",
  "O que se guarda no peito e nunca se gasta.",
];

const FILLER_LETTERS = "AEIOURSTNLMCP".split("");

function normalizeWord(input: string): string {
  // Mantém compat com o normalizador central do schema — usado também no
  // formulário para o preview da palavra. Evita divergência de regras.
  return normalizeWordForGame(input);
}

function extractFirstNames(gift: Partial<Gift>): string[] {
  const candidates: string[] = [];
  for (const raw of [gift.partnerName, gift.recipientName]) {
    if (!raw) continue;
    const first = raw.trim().split(/\s+/)[0] ?? "";
    const normalized = normalizeWord(first);
    if (normalized.length >= 3 && normalized.length <= 8) {
      candidates.push(normalized);
    }
  }
  return candidates;
}

function pickCandidateWords(gift: Partial<Gift>): string[] {
  const customData = getExperienceData<WordGameExperienceData>(gift, "word-game");
  if (Array.isArray(customData?.secretWords)) {
    const customList: string[] = [];
    for (const raw of customData!.secretWords ?? []) {
      if (typeof raw !== "string") continue;
      const normalized = normalizeWord(raw);
      if (normalized.length >= 3 && normalized.length <= 10) {
        customList.push(normalized);
      }
    }
    if (customList.length > 0) return customList;
  }

  const safeNames = extractFirstNames(gift);
  const list = [...FALLBACK_WORDS];
  for (const name of safeNames) {
    if (!list.includes(name)) list.unshift(name);
  }
  return list;
}

function shuffle<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

function generateGridLetters(word: string): string[] {
  const target = Math.max(12, Math.min(16, word.length + 6));
  const letters = [...word];
  while (letters.length < target) {
    letters.push(FILLER_LETTERS[Math.floor(Math.random() * FILLER_LETTERS.length)]);
  }
  return shuffle(letters);
}

function pickSecret(words: string[]): string {
  if (words.length === 0) return FALLBACK_WORDS[0];
  return words[Math.floor(Math.random() * words.length)];
}

function pickHint(gift?: Partial<Gift>): string {
  const customData = gift
    ? getExperienceData<WordGameExperienceData>(gift, "word-game")
    : undefined;
  const customHint = customData?.hint?.trim();
  if (customHint) return customHint;
  return HINTS[Math.floor(Math.random() * HINTS.length)];
}

interface GameRound {
  secret: string;
  letters: string[];
  hint: string;
}

function buildRound(gift: Partial<Gift>): GameRound {
  const candidates = pickCandidateWords(gift);
  const secret = pickSecret(candidates);
  return {
    secret,
    letters: generateGridLetters(secret),
    hint: pickHint(gift),
  };
}

// ─── Componente público ────────────────────────────────────────────────────

export function WordGameSection({
  gift,
  variant,
  palette,
  className,
}: WordGameSectionProps) {
  const [round, setRound] = useState<GameRound>(() => buildRound(gift));
  const [selected, setSelected] = useState<number[]>([]);
  const [feedback, setFeedback] = useState<"idle" | "wrong" | "won">("idle");
  const customTitle =
    getExperienceData<WordGameExperienceData>(gift, "word-game")
      ?.title?.trim() || undefined;

  const answer = useMemo(
    () => selected.map((idx) => round.letters[idx]).join(""),
    [selected, round.letters]
  );

  const secretLength = round.secret.length;

  const handleLetter = useCallback(
    (idx: number) => {
      if (feedback === "won") return;
      setSelected((prev) => {
        if (prev.includes(idx)) return prev;
        if (prev.length >= secretLength) return prev;
        const next = [...prev, idx];
        const built = next.map((i) => round.letters[i]).join("");
        if (built.length === secretLength) {
          setFeedback(built === round.secret ? "won" : "wrong");
        } else {
          setFeedback("idle");
        }
        return next;
      });
    },
    [feedback, secretLength, round.letters, round.secret]
  );

  const handleBackspace = useCallback(() => {
    setSelected((prev) => prev.slice(0, -1));
    setFeedback("idle");
  }, []);

  const handleClear = useCallback(() => {
    setSelected([]);
    setFeedback("idle");
  }, []);

  const handleNext = useCallback(() => {
    setRound(buildRound(gift));
    setSelected([]);
    setFeedback("idle");
  }, [gift]);

  if (variant === "spotify") {
    return (
      <SpotifyVariant
        palette={palette ?? { accent: "#1DB954", accentRgb: "29,185,84" }}
        round={round}
        selected={selected}
        answer={answer}
        secretLength={secretLength}
        feedback={feedback}
        onLetter={handleLetter}
        onBackspace={handleBackspace}
        onClear={handleClear}
        onNext={handleNext}
        className={className}
        title={customTitle}
      />
    );
  }

  return (
    <DefaultVariant
      round={round}
      selected={selected}
      answer={answer}
      secretLength={secretLength}
      feedback={feedback}
      onLetter={handleLetter}
      onBackspace={handleBackspace}
      onClear={handleClear}
      onNext={handleNext}
      className={className}
      title={customTitle}
    />
  );
}

// ─── Tipos compartilhados para as variantes ────────────────────────────────

interface VariantProps {
  round: GameRound;
  selected: number[];
  answer: string;
  secretLength: number;
  feedback: "idle" | "wrong" | "won";
  onLetter: (idx: number) => void;
  onBackspace: () => void;
  onClear: () => void;
  onNext: () => void;
  className?: string;
  title?: string;
}

interface SpotifyVariantProps extends VariantProps {
  palette: WordGamePalette;
}

// ─── Variante default — clara, romântica, premium ──────────────────────────

function DefaultVariant({
  round,
  selected,
  answer,
  secretLength,
  feedback,
  onLetter,
  onBackspace,
  onClear,
  onNext,
  className,
  title,
}: VariantProps) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className={cn(
        "word-game-default relative overflow-hidden rounded-3xl border shadow-sm",
        className
      )}
      style={{
        background:
          "linear-gradient(135deg, #FFE4E6 0%, #FFF7ED 55%, #FFE4F1 100%)",
        borderColor: "rgba(225,29,72,0.18)",
      }}
    >
      <DecorativeBackdrop variant="default" />

      <div className="relative z-10 flex flex-col gap-5 p-6 sm:p-8">
        <header className="flex flex-col items-center gap-2 text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/70 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#881337] shadow-sm backdrop-blur">
            <Puzzle className="h-3 w-3" />
            Jogo de palavras
          </span>
          <h2
            className="text-2xl sm:text-3xl font-semibold leading-tight text-[#1F2937]"
            style={{ fontFamily: "var(--font-serif, serif)" }}
          >
            {title || "Desvende a palavra romântica"}
          </h2>
          <p className="text-sm text-[#1F2937]/75 leading-relaxed max-w-md">
            {round.hint}
          </p>
        </header>

        <AnswerSlots
          selected={selected}
          letters={round.letters}
          secretLength={secretLength}
          feedback={feedback}
          variant="default"
        />

        <LetterGrid
          letters={round.letters}
          selected={selected}
          onLetter={onLetter}
          disabled={feedback === "won"}
          variant="default"
        />

        <ActionsRow
          variant="default"
          canBackspace={selected.length > 0 && feedback !== "won"}
          canClear={selected.length > 0 && feedback !== "won"}
          onBackspace={onBackspace}
          onClear={onClear}
          onNext={onNext}
          showNext={feedback === "won"}
        />

        <FeedbackArea feedback={feedback} secret={round.secret} answer={answer} variant="default" />
      </div>
    </motion.section>
  );
}

// ─── Variante Spotify — imersiva, escura, premium ──────────────────────────

function SpotifyVariant({
  palette,
  round,
  selected,
  answer,
  secretLength,
  feedback,
  onLetter,
  onBackspace,
  onClear,
  onNext,
  className,
  title,
}: SpotifyVariantProps) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={cn(
        "word-game-spotify relative overflow-hidden rounded-2xl",
        className
      )}
      style={{
        background: `radial-gradient(120% 100% at 0% 0%, rgba(${palette.accentRgb},0.22) 0%, rgba(${palette.accentRgb},0.06) 45%, rgba(15,15,18,0.95) 100%)`,
        border: `1px solid rgba(${palette.accentRgb},0.25)`,
      }}
    >
      <DecorativeBackdrop variant="spotify" accent={palette.accent} />

      <div className="relative z-10 flex flex-col gap-4 p-4 sm:p-5">
        <header className="flex items-center gap-2">
          <span style={{ color: palette.accent }}>
            <Puzzle className="h-3.5 w-3.5" />
          </span>
          <span
            className="text-[10px] font-bold uppercase tracking-[0.18em]"
            style={{ color: palette.accent }}
          >
            Jogo de palavras
          </span>
        </header>

        <div className="flex flex-col gap-1">
          <h3 className="text-lg sm:text-xl font-bold text-white leading-tight">
            {title || "Desvende a palavra romântica"}
          </h3>
          <p className="text-[12px] text-white/65 leading-relaxed">
            {round.hint}
          </p>
        </div>

        <AnswerSlots
          selected={selected}
          letters={round.letters}
          secretLength={secretLength}
          feedback={feedback}
          variant="spotify"
          palette={palette}
        />

        <LetterGrid
          letters={round.letters}
          selected={selected}
          onLetter={onLetter}
          disabled={feedback === "won"}
          variant="spotify"
          palette={palette}
        />

        <ActionsRow
          variant="spotify"
          palette={palette}
          canBackspace={selected.length > 0 && feedback !== "won"}
          canClear={selected.length > 0 && feedback !== "won"}
          onBackspace={onBackspace}
          onClear={onClear}
          onNext={onNext}
          showNext={feedback === "won"}
        />

        <FeedbackArea
          feedback={feedback}
          secret={round.secret}
          answer={answer}
          variant="spotify"
          palette={palette}
        />
      </div>
    </motion.section>
  );
}

// ─── Slots de resposta ─────────────────────────────────────────────────────

interface AnswerSlotsProps {
  selected: number[];
  letters: string[];
  secretLength: number;
  feedback: "idle" | "wrong" | "won";
  variant: WordGameVariant;
  palette?: WordGamePalette;
}

function AnswerSlots({
  selected,
  letters,
  secretLength,
  feedback,
  variant,
  palette,
}: AnswerSlotsProps) {
  const slots = Array.from({ length: secretLength }, (_, i) => {
    const idx = selected[i];
    return idx !== undefined ? letters[idx] : "";
  });

  const isSpotify = variant === "spotify";
  const accentRgb = palette?.accentRgb ?? "29,185,84";
  const accent = palette?.accent ?? "#1DB954";

  return (
    <div
      className="flex flex-wrap items-center justify-center gap-1.5 sm:gap-2"
      role="status"
      aria-live="polite"
      aria-label={`Resposta atual com ${selected.length} de ${secretLength} letras`}
    >
      {slots.map((char, i) => {
        const filled = Boolean(char);
        const won = feedback === "won";
        const wrong = feedback === "wrong";

        if (isSpotify) {
          return (
            <span
              key={i}
              className={cn(
                "inline-flex h-9 w-7 sm:h-10 sm:w-8 items-center justify-center rounded-md text-base font-bold transition-all",
                filled ? "scale-100" : "scale-95"
              )}
              style={{
                background: filled
                  ? `rgba(${accentRgb},0.18)`
                  : "rgba(255,255,255,0.04)",
                border: won
                  ? `1px solid ${accent}`
                  : wrong
                  ? "1px solid rgba(244,63,94,0.55)"
                  : `1px solid rgba(${accentRgb},${filled ? 0.4 : 0.18})`,
                color: won ? accent : "#ffffff",
                boxShadow: won
                  ? `0 0 18px rgba(${accentRgb},0.55)`
                  : "none",
              }}
            >
              {char || "·"}
            </span>
          );
        }

        return (
          <span
            key={i}
            className={cn(
              "inline-flex h-10 w-8 sm:h-11 sm:w-9 items-center justify-center rounded-lg text-base font-bold transition-all",
              filled ? "scale-100" : "scale-95"
            )}
            style={{
              background: filled ? "#ffffff" : "rgba(255,255,255,0.55)",
              border: won
                ? "1px solid #E11D48"
                : wrong
                ? "1px solid rgba(244,63,94,0.55)"
                : "1px solid rgba(225,29,72,0.25)",
              color: won ? "#E11D48" : "#1F2937",
              boxShadow: won
                ? "0 6px 20px rgba(225,29,72,0.18)"
                : filled
                ? "0 1px 3px rgba(0,0,0,0.06)"
                : "none",
            }}
          >
            {char || "·"}
          </span>
        );
      })}
    </div>
  );
}

// ─── Grade de letras ───────────────────────────────────────────────────────

interface LetterGridProps {
  letters: string[];
  selected: number[];
  onLetter: (idx: number) => void;
  disabled: boolean;
  variant: WordGameVariant;
  palette?: WordGamePalette;
}

function LetterGrid({
  letters,
  selected,
  onLetter,
  disabled,
  variant,
  palette,
}: LetterGridProps) {
  const isSpotify = variant === "spotify";
  const accentRgb = palette?.accentRgb ?? "29,185,84";
  const accent = palette?.accent ?? "#1DB954";

  return (
    <div
      className="grid gap-2 mx-auto w-full max-w-md"
      style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}
    >
      {letters.map((letter, idx) => {
        const isSelected = selected.includes(idx);
        const isLocked = isSelected || disabled;

        if (isSpotify) {
          return (
            <button
              key={idx}
              type="button"
              onClick={() => onLetter(idx)}
              disabled={isLocked}
              aria-label={`Letra ${letter}${isSelected ? " (selecionada)" : ""}`}
              aria-pressed={isSelected}
              className={cn(
                "aspect-square w-full rounded-xl text-base sm:text-lg font-bold transition-all duration-200",
                "disabled:cursor-not-allowed",
                !isLocked && "hover:scale-[1.04] active:scale-95"
              )}
              style={{
                background: isSelected
                  ? `rgba(${accentRgb},0.25)`
                  : "rgba(255,255,255,0.05)",
                color: isSelected ? accent : "rgba(255,255,255,0.85)",
                border: isSelected
                  ? `1px solid ${accent}`
                  : `1px solid rgba(${accentRgb},0.15)`,
                boxShadow: isSelected
                  ? `0 0 16px rgba(${accentRgb},0.45)`
                  : "none",
                opacity: disabled && !isSelected ? 0.5 : 1,
              }}
            >
              {letter}
            </button>
          );
        }

        return (
          <button
            key={idx}
            type="button"
            onClick={() => onLetter(idx)}
            disabled={isLocked}
            aria-label={`Letra ${letter}${isSelected ? " (selecionada)" : ""}`}
            aria-pressed={isSelected}
            className={cn(
              "aspect-square w-full rounded-xl text-base sm:text-lg font-bold transition-all duration-200",
              "disabled:cursor-not-allowed",
              !isLocked && "hover:-translate-y-0.5 active:translate-y-0"
            )}
            style={{
              background: isSelected
                ? "linear-gradient(135deg, #E11D48 0%, #BE123C 100%)"
                : "#ffffff",
              color: isSelected ? "#ffffff" : "#1F2937",
              border: isSelected
                ? "1px solid #BE123C"
                : "1px solid rgba(225,29,72,0.18)",
              boxShadow: isSelected
                ? "0 8px 20px rgba(225,29,72,0.28)"
                : "0 1px 3px rgba(0,0,0,0.06)",
              opacity: disabled && !isSelected ? 0.6 : 1,
            }}
          >
            {letter}
          </button>
        );
      })}
    </div>
  );
}

// ─── Linha de ações ────────────────────────────────────────────────────────

interface ActionsRowProps {
  variant: WordGameVariant;
  palette?: WordGamePalette;
  canBackspace: boolean;
  canClear: boolean;
  onBackspace: () => void;
  onClear: () => void;
  onNext: () => void;
  showNext: boolean;
}

function ActionsRow({
  variant,
  palette,
  canBackspace,
  canClear,
  onBackspace,
  onClear,
  onNext,
  showNext,
}: ActionsRowProps) {
  const isSpotify = variant === "spotify";
  const accentRgb = palette?.accentRgb ?? "29,185,84";
  const accent = palette?.accent ?? "#1DB954";

  if (isSpotify) {
    return (
      <div className="flex flex-wrap items-center justify-center gap-2">
        <button
          type="button"
          onClick={onBackspace}
          disabled={!canBackspace}
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider transition disabled:opacity-40 disabled:cursor-not-allowed"
          style={{
            color: "rgba(255,255,255,0.85)",
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.14)",
          }}
        >
          <Delete className="h-3.5 w-3.5" />
          Apagar
        </button>
        <button
          type="button"
          onClick={onClear}
          disabled={!canClear}
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider transition disabled:opacity-40 disabled:cursor-not-allowed"
          style={{
            color: "rgba(255,255,255,0.85)",
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.14)",
          }}
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Limpar
        </button>
        {showNext ? (
          <button
            type="button"
            onClick={onNext}
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider transition hover:opacity-90 active:scale-95"
            style={{
              background: accent,
              color: "#0F0F12",
              boxShadow: `0 6px 18px rgba(${accentRgb},0.4)`,
            }}
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Próxima
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center justify-center gap-2">
      <button
        type="button"
        onClick={onBackspace}
        disabled={!canBackspace}
        className="inline-flex items-center gap-1.5 rounded-full bg-white/70 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-[#881337] shadow-sm backdrop-blur transition hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <Delete className="h-3.5 w-3.5" />
        Apagar
      </button>
      <button
        type="button"
        onClick={onClear}
        disabled={!canClear}
        className="inline-flex items-center gap-1.5 rounded-full bg-white/70 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-[#881337] shadow-sm backdrop-blur transition hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <RotateCcw className="h-3.5 w-3.5" />
        Limpar
      </button>
      {showNext ? (
        <button
          type="button"
          onClick={onNext}
          className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-white shadow-md transition hover:opacity-90 active:scale-95"
          style={{
            background:
              "linear-gradient(135deg, #E11D48 0%, #BE123C 100%)",
            boxShadow: "0 8px 22px rgba(225,29,72,0.32)",
          }}
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Próxima palavra
        </button>
      ) : null}
    </div>
  );
}

// ─── Área de feedback ──────────────────────────────────────────────────────

interface FeedbackAreaProps {
  feedback: "idle" | "wrong" | "won";
  secret: string;
  answer: string;
  variant: WordGameVariant;
  palette?: WordGamePalette;
}

function FeedbackArea({
  feedback,
  variant,
  palette,
}: FeedbackAreaProps) {
  const isSpotify = variant === "spotify";
  const accent = palette?.accent ?? "#1DB954";

  return (
    <AnimatePresence mode="wait">
      {feedback === "won" ? (
        <motion.div
          key="won"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          className={cn(
            "flex flex-col items-center gap-1.5 text-center",
            isSpotify ? "text-[12px]" : "text-sm"
          )}
        >
          <div className="flex items-center gap-1.5">
            {isSpotify ? (
              <>
                <Sparkles className="h-4 w-4" style={{ color: accent }} />
                <span
                  className="font-bold uppercase tracking-[0.18em]"
                  style={{ color: accent }}
                >
                  Você acertou
                </span>
                <Sparkles className="h-4 w-4" style={{ color: accent }} />
              </>
            ) : (
              <>
                <Heart className="h-4 w-4 fill-[#E11D48] text-[#E11D48]" />
                <span className="font-bold uppercase tracking-[0.18em] text-[#E11D48]">
                  Você acertou
                </span>
                <Heart className="h-4 w-4 fill-[#E11D48] text-[#E11D48]" />
              </>
            )}
          </div>
          <p
            className={cn(
              "leading-relaxed",
              isSpotify ? "text-white/75" : "text-[#1F2937]/80"
            )}
          >
            Algumas palavras parecem feitas para histórias como a de vocês.
          </p>
        </motion.div>
      ) : feedback === "wrong" ? (
        <motion.div
          key="wrong"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          className={cn(
            "text-center",
            isSpotify
              ? "text-[11px] text-white/65"
              : "text-xs text-[#881337]/80"
          )}
          role="alert"
        >
          Quase lá — tente novamente, sem pressa.
        </motion.div>
      ) : (
        <motion.div
          key="idle"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className={cn(
            "text-center",
            isSpotify
              ? "text-[10px] uppercase tracking-[0.18em] text-white/40"
              : "text-[11px] uppercase tracking-[0.18em] text-[#881337]/55"
          )}
        >
          Toque nas letras para montar a palavra
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ─── Decorações ────────────────────────────────────────────────────────────

function DecorativeBackdrop({
  variant,
  accent,
}: {
  variant: WordGameVariant;
  accent?: string;
}) {
  if (variant === "spotify") {
    return (
      <>
        <Heart
          className="absolute -top-2 -right-2 h-14 w-14 opacity-10"
          style={{ color: accent ?? "#1DB954" }}
          fill="currentColor"
        />
        <Sparkles
          className="absolute bottom-3 left-3 h-4 w-4 opacity-25"
          style={{ color: accent ?? "#1DB954" }}
        />
      </>
    );
  }
  return (
    <>
      <Heart
        className="absolute -top-4 -right-4 h-24 w-24 text-[#E11D48]/15"
        fill="currentColor"
      />
      <Heart
        className="absolute -bottom-3 -left-3 h-14 w-14 text-[#F59E0B]/25"
        fill="currentColor"
      />
      <Sparkles className="absolute top-4 left-4 h-4 w-4 text-[#E11D48]/40" />
    </>
  );
}
